/* KOR-E — product card variant swatches.
   Loaded once, globally (layout/theme.liquid), so it covers every product card on the site —
   snippets/card-product.liquid's shared markup (Shop, Collections, Search, Related products,
   Recommendations) and sections/kore-best-selling-products-carousel.liquid's own bespoke card
   markup alike — via plain event delegation on document, no per-section wiring needed.

   ROUND 2 — clicking a swatch now stays on the page instead of navigating to the product page:
     1. the clicked swatch becomes active
     2. the card's own main image is swapped to that variant's image, carried server-side in a
        data attribute on the swatch — never the swatch's own small icon image (data-variant-img).
     3. if that variant has no image to swap to, the data attribute is empty and the card's
        current image is deliberately left exactly as it was — no fallback to the swatch icon,
        no broken image.
     4. the card's own product/title links (but never a sibling swatch's own link) are updated
        to carry ?variant=ID, but ONLY when their current href is an exact match for this card's
        own product URL (data-kore-product-url) — a merchant-set custom button link in the
        carousel, which can point anywhere else, is left untouched automatically.

   ROUND 3 — perceived click-to-swap delay. Measured directly (performance.now() around the
   actual src assignment and the image's own load event, not guessed): the click handler itself
   sets img.src within ~7ms of the click — there is no JS debounce/timeout/await anywhere in
   this file adding delay. The entire ~140ms felt-lag was the browser fetching+decoding an image
   it had NEVER requested before, because today was the first time that URL was ever referenced
   on the page. The active-state border already applies synchronously in the same tick as the
   click (no delay there) — the fix below targets the one real cost, by warming the browser's
   own HTTP cache (and, where supported, pre-decoding) for a swatch's target image the moment
   real user intent appears — pointerover (desktop hover) or touchstart (mobile, fires before
   click in a tap gesture) — rather than only starting that fetch at click time. This is
   deliberately NOT a blanket preload of every swatch on the page (which would cost real bytes
   for images a visitor may never choose): only the specific swatch the pointer is actually over
   or touching is ever requested, and each URL is only ever requested once (preloadedUrls). No
   artificial loading indicator is used — this fixes the real cause instead of masking it.

   ROUND 4 — click-to-swap source simplified from custom.variant_gallery[0] to the Official
   Shopify Variant Image (variant.featured_image), carried in data-variant-official-img. The
   gallery metafield required its own lookup/fallback chain server-side and was reported slow/
   inconsistent in practice; the Official Variant Image is the catalog's own primary image for
   the variant, simpler and always the right one to show. data-variant-img is NOT reused for
   this — it still carries the swatch's own icon source (custom.variant_swatch_image, falling
   back to featured_image), which is a deliberately different, often close-up/texture image and
   would look wrong blown up as the card's main photo once variant_swatch_image is populated with
   real data. */
(function () {
  function closestCard(el) {
    return el.closest('.card-wrapper, .kore-bsp__card');
  }

  function mainImageFor(card) {
    // snippets/card-product.liquid structure
    var img = card.querySelector('.card__media .media img');
    if (img) return img;
    // sections/kore-best-selling-products-carousel.liquid structure — primary image only,
    // never the separate hover-reveal secondary image
    return card.querySelector('.kore-bsp__img:not(.kore-bsp__img--secondary)');
  }

  var preloadedUrls = Object.create(null);
  function preloadVariantImage(url) {
    if (!url || preloadedUrls[url]) return;
    preloadedUrls[url] = true;
    var img = new Image();
    img.src = url;
    // Decode ahead of time too where supported, so the actual swap (img.src = url on the real,
    // visible <img>) can paint immediately from an already-decoded frame instead of decoding on
    // the critical path of the click. Best-effort only — a rejected/unsupported decode() still
    // leaves the plain network preload above in place.
    if (img.decode) {
      img.decode().catch(function () {});
    }
  }

  function handlePreloadTrigger(e) {
    var btn = e.target.closest && e.target.closest('.kore-swatch');
    if (!btn) return;
    preloadVariantImage(btn.getAttribute('data-variant-official-img'));
  }
  // pointerover (not pointerenter) because it bubbles, so one delegated listener covers every
  // swatch on the page without per-element wiring; re-firing while the pointer moves within the
  // same swatch is a harmless no-op thanks to preloadedUrls.
  document.addEventListener('pointerover', handlePreloadTrigger);
  // touchstart fires at the very start of a tap, ahead of the click event itself — passive:true
  // since this only ever starts a side-effect fetch and never calls preventDefault here, so it
  // can't interfere with scrolling or the browser's own fast-tap handling.
  document.addEventListener('touchstart', handlePreloadTrigger, { passive: true });

  // ROUND 6 — "+N" reveal. Every variant beyond the visible limit is already in the markup
  // (.kore-swatch--extra, hidden by CSS alone — see assets/kore-swatches-global.css), so
  // revealing them needs no fetch/injection/re-init: just drop .kore-swatches--expanded on the
  // group and the existing CSS rule stops hiding them. The newly-visible swatches already carry
  // the exact same .kore-swatch markup/data-attributes as the first 5, so the click delegation
  // below picks them up automatically — nothing else to wire up. +N never selects a variant,
  // never swaps the image, never navigates.
  document.addEventListener('click', function (e) {
    var moreBtn = e.target.closest('.kore-swatch-more');
    if (moreBtn) {
      e.preventDefault();
      e.stopPropagation();
      var moreGroup = moreBtn.closest('.kore-swatches');
      if (moreGroup) moreGroup.classList.add('kore-swatches--expanded');
      moreBtn.remove();
      return;
    }

    var btn = e.target.closest('.kore-swatch');
    if (!btn) return;

    // Let a modified click (open in new tab, etc.) use the swatch's own real href as normal.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var card = closestCard(btn);
    if (!card) return;

    e.preventDefault();
    e.stopPropagation();

    var group = btn.closest('.kore-swatches');
    if (group) {
      group.querySelectorAll('.kore-swatch').forEach(function (s) {
        s.classList.remove('is-active');
      });
    }
    btn.classList.add('is-active');

    var officialImg = btn.getAttribute('data-variant-official-img');
    if (officialImg) {
      var mainImg = mainImageFor(card);
      if (mainImg) {
        // No race-condition guard is needed for rapid clicks across different swatches: each
        // click is handled fully, synchronously, start to finish (no await/timeout anywhere in
        // this handler) before the next click event is even dispatched, and re-assigning
        // img.src always supersedes whatever the previous assignment was still fetching — the
        // browser only ever paints the LATEST src a given <img> was given, never an earlier,
        // slower-arriving one. The preload above already means the common case (hovered/touched
        // before clicking) resolves this assignment from cache instantly. Both src and srcset are
        // set together so a stale multi-width srcset never overrides the freshly-set src — a
        // bare URL with no width/density descriptor in srcset is valid and defaults to 1x.
        mainImg.src = officialImg;
        mainImg.srcset = officialImg;
      }
    }

    var productUrl = card.getAttribute('data-kore-product-url');
    var variantId = btn.getAttribute('data-variant-id');
    if (productUrl && variantId) {
      var newHref = productUrl + '?variant=' + variantId;
      card.querySelectorAll('a[href]').forEach(function (a) {
        if (a.classList.contains('kore-swatch')) return;
        var href = a.getAttribute('href');
        if (href && href.split('?')[0] === productUrl) {
          a.setAttribute('href', newHref);
        }
      });
    }
  });
})();
